"use client";

// Frontera de error de la app (App Router). Sin ella, cualquier excepción de
// render tumba el árbol entero y Next pinta su pantalla genérica ("Application
// error: a client-side exception has occurred"), que no dice nada y solo se
// resuelve recargando.
//
// Aquí se recupera sin perder la sesión (`reset()` re-renderiza el segmento) y,
// sobre todo, se deja rastro: el error se manda a la consola con su `digest`,
// que es lo único que correlaciona el fallo del navegador con el bundle de
// producción. Al reportar un error, pedir ese identificador.

import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import {
  formatClientErrors,
  recordClientError,
} from "@/services/clientErrorLog";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // El stack completo solo existe en el objeto: la pantalla no lo muestra.
    console.error("[app-error]", error?.digest ?? "sin digest", error);
    // Y al buzón local, para poder pedirlo después aunque se cierre la consola.
    recordClientError({
      kind: "boundary",
      message: error?.message ?? "sin mensaje",
      stack: error?.stack,
      digest: error?.digest,
    });
  }, [error]);

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(formatClientErrors());
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="section is-dashboard">
      <div className="padding-2xl padding-position-vertical">
        <div className="w-layout-grid grid is-container">
          <div className="division start-02 span-12">
            <div className="flex vertical gap-s" style={{ height: "auto" }}>
              <h1 className="title w-variant-dc2b7054-59b2-e756-ec66-0d0dff02932c">
                Algo salió mal
              </h1>
              <p className="font-family-body text-size-2xs">
                La página no se pudo mostrar. Puedes reintentar; si vuelve a
                pasar, recarga o repórtalo con el identificador de abajo.
              </p>
              {error?.digest ? (
                <p
                  className="font-family-body text-size-3xs"
                  style={{ color: "var(--brand-main--dark-grey)" }}
                >
                  Identificador del error: {error.digest}
                </p>
              ) : null}
              <div
                className="gap-2xs"
                style={{ display: "flex", flexWrap: "wrap" }}
              >
                <Button
                  variant="solid"
                  type="button"
                  onClick={reset}
                  style={{ cursor: "pointer" }}
                >
                  Reintentar
                </Button>
                <Button
                  variant="transparent"
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{ cursor: "pointer" }}
                >
                  Recargar la página
                </Button>
                {/* Copia el buzón local (este error y los anteriores, con su
                    stack) para pegarlo en el reporte: sin esto, el detalle solo
                    vive en la consola del navegador de quien lo vio. */}
                <Button
                  variant="text"
                  type="button"
                  onClick={copyReport}
                  style={{ cursor: "pointer" }}
                >
                  {copied ? "Detalles copiados" : "Copiar detalles del error"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
